import { useState, useEffect, memo, useCallback } from "react";
import AnimacionCarga from "../components/animacion_de_carga";
import TablaGenerica from "../components/tablas";
import { obtenerVisitantes, obtenerDatosConjunto } from "../../functions/consultas";
import BotonSOS from "./sos/boton_sos";
import Swal from "sweetalert2";
import { crearRegistro, actualizarRegistro, actualizarMultiple, consultarSimple } from "../../functions/crud";
import { descargarPlantillaVisitantes } from "../../functions/importar_visitantes";
import { asistenteCargarVisitantes, historialPase, menuAccionesVisitante, menuArchivosVisitantes } from "./dialogos_visitantes";
import {
  BarraEstados, BotonArchivos, abrirMenuArchivos, botonOpciones, eliminarConConfirmacion, filtrarPorEstado, soloDelResidente,
} from "./listado_comun";
import { generarFactura } from "./generar_factura";
import { correoDeVivienda, enviarCorreoVisitante, mensajeNotificacion } from "../../functions/correos_backend";
import { enviarPasePorCorreo, ROLES_ENVIO_PASE } from "./enviar_pase_correo";
import {
  TIPOS_DOCUMENTO,
  TIPOS_VEHICULO,
  ESTADOS,
  TIPOS_INGRESO,
  esPorteria,
  fechaHoraDart,
  fechaDart,
  horaDart,
  rutaPase,
  rutaHistoricoPase,
  nitDePase,
  movimientosHistorial,
  obtenerYearMes,
  limpiarDireccion,
  nuevoPaseWeb,
  cambiosNuevoPase,
  construirDireccion,
  opcionesVivienda,
  extraerNumero,
  formatearFechaListado,
  motivoNoGestionable,
} from "../../functions/pase_visitante";
import "bootstrap-icons/font/bootstrap-icons.css";

// label va en el Excel exportado (la carga masiva lo reconoce); corto es el título de la tabla.
const COLUMNAS = [
  { label: "Dirección", key: "casa", estilo: { whiteSpace: "nowrap" } },
  { label: "Tipo", key: "tipoVisitante" },
  { label: "Nombre invitado", corto: "N. invitado", key: "nombreDocumentoVisitante" },
  // width 1% deja la columna del ancho de su título (que no se parte); el contenido baja de línea.
  { label: "Tipo documento", corto: "T. documento", key: "tipoDocumentoVisitante", estilo: { width: "1%" } },
  { label: "N° documento", corto: "N. documento", key: "numeroDocumentoVisitante" },
  { label: "Tipo pase", corto: "T. pase", key: "tipoPermiso" },
  { label: "Tipo vehículo", corto: "T. ingreso", key: "tipoVehiculo" },
  { label: "Placa", key: "placasVehiculo", estilo: { whiteSpace: "nowrap", minWidth: 100 } },
  { label: "Estado", key: "estado" },
  { label: "Fecha creación", corto: "F. creación", key: "fechaCreacionRegistro" },
  { label: "Fecha ingreso", corto: "F. ingreso", key: "fechaHoraIngreso" },
  { label: "Fecha salida", corto: "F. salida", key: "fechaHoraSalida" },
];

// "2026-07-05 12:48 PM" se muestra con la fecha arriba y la hora debajo.
const fechaEnDosLineas = (valor) => {
  if (!valor) return "";
  const espacio = valor.indexOf(" ");
  if (espacio < 0) return valor;
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {valor.slice(0, espacio)}<br />
      <span style={{ color: "#6b6b70" }}>{valor.slice(espacio + 1)}</span>
    </span>
  );
};

const RENDERIZADORES = {
  fechaCreacionRegistro: fechaEnDosLineas,
  fechaHoraIngreso: fechaEnDosLineas,
  fechaHoraSalida: fechaEnDosLineas,
};

// Correo al residente cuando el visitante ingresa o sale, igual que la app
// (crear_visitante.dart y detalle_pase.dart → _notificarResidente). Devuelve el aviso para la ventana final.
const notificarResidente = async (nit, pase, estado) => {
  const correo = await correoDeVivienda(nit, pase.casa);
  if (!correo) return mensajeNotificacion({ correo });
  const enviado = await enviarCorreoVisitante({
    correo,
    nombreVisitante: pase.nombreDocumentoVisitante,
    tipoDocumento: pase.tipoDocumentoVisitante,
    numeroDocumento: pase.numeroDocumentoVisitante,
    direccion: pase.casa,
    tipoVisitante: pase.tipoVisitante,
    autorizo: pase.usuario,
    estado,
    fechaHora: fechaHoraDart().substring(0, 19),
  });
  return mensajeNotificacion({ correo, enviado });
};

// Como en la app, solo portería elige la vivienda y escribe quién autorizó; el residente
// (y el empleado) crea el pase para su propia dirección y queda como quien autoriza.
const eligeVivienda = () => esPorteria();

const ListadoVisitantes = memo(() => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState(null);

  const capturarDatos = useCallback(async () => {
    try {
      const data = await obtenerVisitantes();
      const lista = data.map(item => ({
        ...item,
        fechaCreacionRegistroISO: item.fechaCreacionRegistro,
        fechaHoraIngresoISO: item.fechaHoraIngreso,
        fechaHoraSalidaISO: item.fechaHoraSalida,
        fechaCreacionRegistro: formatearFechaListado(item.fechaCreacionRegistro),
        fechaHoraIngreso: formatearFechaListado(item.fechaHoraIngreso),
        fechaHoraSalida: formatearFechaListado(item.fechaHoraSalida),
      }));
      setDatos(soloDelResidente(lista, "casa"));
    } catch (error) {
      console.error("Error al obtener visitantes:", error);
    } finally {
      setCargando(false);
    }
  }, []);

  const [moduloParqueadero, setModuloParqueadero] = useState(true);
  useEffect(() => {
    obtenerDatosConjunto().then((c) => setModuloParqueadero(c?.moduloParqueadero !== false));
  }, []);
  const puedeFacturar = () => moduloParqueadero && esPorteria();

  const generarFormularioHTML = (datosVisitante = null) => {
    const editando = Boolean(datosVisitante);

    const torreValue = editando ? extraerNumero(datosVisitante.casa, 'TORRE') : '';
    const apartamentoValue = editando ? extraerNumero(datosVisitante.casa, 'APARTAMENTO') : '';
    const manzanaValue = editando ? extraerNumero(datosVisitante.casa, 'MANZANA') : '';
    const casaValue = editando ? extraerNumero(datosVisitante.casa, 'CASA') : '';

    const esApartamento = datosVisitante?.casa?.includes('TORRE');
    const tipoCasaInicial = editando && !esApartamento ? 'CASA' : 'APARTAMENTO';

    const opciones = opcionesVivienda();
    const viviendaInicial = editando && opciones.includes(datosVisitante.casa)
      ? datosVisitante.casa
      : 'ADICIONAR MANUALMENTE';
    const manual = viviendaInicial === 'ADICIONAR MANUALMENTE';

    const direccionInicial = editando
      ? datosVisitante.casa || ''
      : construirDireccion(tipoCasaInicial, '', '');

    return `
    <style>
      .swal2-input, .swal2-select {
        margin: 0 !important;
        width: 100% !important;
        box-sizing: border-box !important;
        padding: 10px !important;
        border: 1px solid #d1d5db !important;
        border-radius: 5px !important;
        font-size: 14px !important;
      }
      .form-row { display: flex; gap: 12px; margin-bottom: 15px; }
      .form-col { flex: 1; }
      .campo-label {
        display: block; margin-bottom: 6px;
        color: #555; font-size: 14px; font-weight: 500;
      }
    </style>
    <div style="text-align:left;padding:20px;">
      <h3 style="margin:0 0 25px;color:#2c3e50;font-size:20px;font-weight:600;">
        ${editando ? 'Editar Pase' : 'Crear Visitantes'}
      </h3>

      ${!eligeVivienda() ? `
      <div style="margin-bottom:15px;">
        <label class="campo-label">Dirección de la vivienda</label>
        <input class="swal2-input" readonly value="${localStorage.getItem("Direccion") || ""}"
          style="background:#d3d3d3;cursor:not-allowed;">
      </div>` : ''}

      <div style="margin-bottom:15px;display:${eligeVivienda() ? 'block' : 'none'};">
        <label class="campo-label">Dirección de la vivienda</label>
        <select id="selectorVivienda" class="swal2-select">
          ${opciones.map(op =>
            `<option value="${op}" ${op === viviendaInicial ? 'selected' : ''}>${op}</option>`
          ).join('')}
        </select>
      </div>

      <div id="bloqueVivienda" style="display:${manual && eligeVivienda() ? 'block' : 'none'};">
        <div class="form-row">
          <div class="form-col">
            <label class="campo-label">Tipo de vivienda</label>
            <select id="tipoCasa" class="swal2-select">
              <option value="APARTAMENTO" ${tipoCasaInicial === 'APARTAMENTO' ? 'selected' : ''}>APARTAMENTO</option>
              <option value="CASA" ${tipoCasaInicial === 'CASA' ? 'selected' : ''}>CASA</option>
            </select>
          </div>
          <div class="form-col">
            <label class="campo-label">No Torre/Etapa</label>
            <input id="indicadorSector" class="swal2-input" placeholder="Número"
              value="${tipoCasaInicial === 'APARTAMENTO' ? torreValue : manzanaValue}">
          </div>
          <div class="form-col">
            <label class="campo-label">No Casa</label>
            <input id="indicadorCasa" class="swal2-input" placeholder="Número"
              value="${tipoCasaInicial === 'APARTAMENTO' ? apartamentoValue : casaValue}">
          </div>
        </div>

        <div style="margin-bottom:15px;">
          <label class="campo-label">Dirección</label>
          <input id="direccionArmada" class="swal2-input" readonly
            value="${direccionInicial}"
            style="background:#d3d3d3;cursor:not-allowed;">
        </div>
      </div>

      <div style="margin-bottom:15px;display:${eligeVivienda() ? 'block' : 'none'};">
        <label class="campo-label">Autorizó Ingreso</label>
        <input id="quienAutoriza" class="swal2-input" placeholder="Nombre completo"
          value="${datosVisitante?.usuario || ''}">
      </div>

      <div style="margin-bottom:15px;">
        <label class="campo-label">Nombre del visitante</label>
        <input id="nombreVisitante" class="swal2-input" placeholder="Nombre completo"
          value="${datosVisitante?.nombreDocumentoVisitante || ''}">
      </div>

      <div class="form-row">
        <div class="form-col">
          <label class="campo-label">Tipo de documento</label>
          <select id="tipoDocumento" class="swal2-select">
            ${TIPOS_DOCUMENTO.map(tipo =>
              `<option value="${tipo}" ${(datosVisitante?.tipoDocumentoVisitante || TIPOS_DOCUMENTO[0]) === tipo ? 'selected' : ''}>${tipo}</option>`
            ).join('')}
          </select>
        </div>
        <div class="form-col">
          <label class="campo-label">Cédula</label>
          <input id="numeroDocumento" class="swal2-input" placeholder="Mínimo 6 caracteres"
            value="${datosVisitante?.numeroDocumentoVisitante || ''}">
        </div>
      </div>

      <div class="form-row">
        <div class="form-col">
          <label class="campo-label">Tipo de vehículo</label>
          <select id="tipoVehiculo" class="swal2-select">
            ${TIPOS_VEHICULO.map(tipo =>
              `<option value="${tipo}" ${(datosVisitante?.tipoVehiculo || TIPOS_VEHICULO[0]) === tipo ? 'selected' : ''}>${tipo}</option>`
            ).join('')}
          </select>
        </div>
        <div class="form-col">
          <label class="campo-label">Placa</label>
          <input id="placaVehiculo" class="swal2-input" placeholder="ABC123"
            value="${datosVisitante?.placasVehiculo || ''}" style="text-transform:uppercase;">
        </div>
      </div>
    </div>
  `;
  };

  const configurarEventosFormulario = () => {
    const selector = document.getElementById("selectorVivienda");
    const bloque = document.getElementById("bloqueVivienda");
    const tipoCasa = document.getElementById("tipoCasa");
    const sector = document.getElementById("indicadorSector");
    const numero = document.getElementById("indicadorCasa");
    const direccion = document.getElementById("direccionArmada");

    const refrescarDireccion = () => {
      direccion.value = construirDireccion(tipoCasa.value, sector.value, numero.value);
    };

    const alternarBloque = () => {
      bloque.style.display =
        selector.value === "ADICIONAR MANUALMENTE" && eligeVivienda() ? "block" : "none";
    };

    selector.addEventListener("change", alternarBloque);
    [tipoCasa, sector, numero].forEach(campo =>
      campo.addEventListener("input", refrescarDireccion)
    );
    tipoCasa.addEventListener("change", refrescarDireccion);
  };

  const validarYObtenerDatos = () => {
    const vivienda = document.getElementById("selectorVivienda").value;
    const quienAutoriza = document.getElementById("quienAutoriza").value.trim();
    const nombreVisitante = document.getElementById("nombreVisitante").value.trim();
    const tipoDocumento = document.getElementById("tipoDocumento").value;
    const numeroDocumento = document.getElementById("numeroDocumento").value.trim();
    const tipoVehiculo = document.getElementById("tipoVehiculo").value;
    const placasVehiculo = document.getElementById("placaVehiculo").value.trim().toUpperCase();

    if (eligeVivienda() && !quienAutoriza) {
      Swal.showValidationMessage("Debe ingresar quien autorizó el ingreso");
      return false;
    }
    if (!nombreVisitante) {
      Swal.showValidationMessage("Ingrese Nombre del visitante");
      return false;
    }
    if (!tipoDocumento) {
      Swal.showValidationMessage("Debe seleccionar el tipo de documento");
      return false;
    }
    if (numeroDocumento.length < 6) {
      Swal.showValidationMessage("Mínimo seis caracteres");
      return false;
    }

    let direccion;
    if (!eligeVivienda()) {
      direccion = localStorage.getItem("Direccion") || "";
    } else if (vivienda === "ADICIONAR MANUALMENTE") {
      const tipoCasa = document.getElementById("tipoCasa").value;
      const sector = document.getElementById("indicadorSector").value.trim();
      const numero = document.getElementById("indicadorCasa").value.trim();

      if (!numero) {
        Swal.showValidationMessage("Debe seleccionar la vivienda destino");
        return false;
      }
      direccion = construirDireccion(tipoCasa, sector, numero);
    } else {
      direccion = vivienda;
    }

    return {
      casa: direccion,
      usuario: eligeVivienda() ? quienAutoriza : (localStorage.getItem("NombreCompleto") || ""),
      nombreDocumentoVisitante: nombreVisitante,
      tipoDocumentoVisitante: tipoDocumento,
      numeroDocumentoVisitante: numeroDocumento,
      tipoVehiculo,
      placasVehiculo,
      existeVehiculo: placasVehiculo.length > 0,
    };
  };
  const crearVisitante = useCallback(async () => {
    const { value: formValues } = await Swal.fire({
      title: "Crear Visitantes",
      html: generarFormularioHTML(),
      didOpen: () => configurarEventosFormulario(),
      showCancelButton: true,
      confirmButtonText: "Crear pase",
      cancelButtonText: "Cancelar",
      width: 600,
      preConfirm: () => {
        const datos = validarYObtenerDatos();
        if (!datos) return false;

        const ahora = fechaHoraDart();
        const estado = esPorteria() ? "Ingreso" : "Pendiente";

        return nuevoPaseWeb({
          ...datos,
          estado,
          fechaCreacionRegistro: ahora,
          fechaHoraIngreso: estado === "Ingreso" ? ahora : null,
          fechaTemporalPermiso: fechaDart(),
          horaTemporalPermiso: horaDart(),
        });
      }
    });

    if (!formValues) return;

    try {
      const nit = localStorage.getItem("CodigoCliente") || "900362633";
      await actualizarMultiple(cambiosNuevoPase(nit, formValues));

      const aviso = formValues.estado === "Ingreso"
        ? await notificarResidente(nit, formValues, "Ingreso")
        : "";

      Swal.fire({ title: "Éxito", html: `Pase creado correctamente${aviso}`, icon: "success", confirmButtonText: "Aceptar" });
      capturarDatos();
    } catch (error) {
      console.error("Error al crear visitante:", error);
      Swal.fire("Error", "No se pudo crear el pase", "error");
    }
  }, [capturarDatos]);

  const editarVisitante = useCallback(async (visitante) => {
    if (visitante.estado === "Anulado") {
      Swal.fire({
        title: "Pase anulado",
        text: "Este pase ya fue anulado y no puede modificarse.",
        icon: "info",
        confirmButtonText: "Cerrar"
      });
      return;
    }

    if (!visitante.id) {
      Swal.fire("Error", "El registro no tiene un ID válido", "error");
      return;
    }

    const motivo = motivoNoGestionable(visitante);
    if (motivo) {
      Swal.fire({ title: "No se puede gestionar", text: motivo, icon: "info", confirmButtonText: "Cerrar" });
      return;
    }

    const estadoSugerido = visitante.estado === "Ingreso"
      ? "Salida"
      : visitante.estado === "Pendiente"
      ? "Ingreso"
      : "Salida";

    const tipoIngresoActual = visitante.tipoIngreso
      || (visitante.placasVehiculo ? "Vehiculo" : "Peatonal");

    const tipoVehiculoActual = TIPOS_VEHICULO.includes(visitante.tipoVehiculo)
      ? visitante.tipoVehiculo
      : "CARRO";

    const opcion = (valor, actual) =>
      `<option value="${valor}" ${valor === actual ? "selected" : ""}>${valor}</option>`;

    const { value: datos } = await Swal.fire({
      title: "Gestionar pase",
      width: 560,
      html: `
        <style>
          #form-gestion .swal2-input, #form-gestion .swal2-select {
            margin: 0 !important; width: 100% !important; box-sizing: border-box !important;
            padding: 10px !important; border: 1px solid #d1d5db !important;
            border-radius: 5px !important; font-size: 14px !important;
          }
          #form-gestion .fila { display:flex; gap:12px; margin-bottom:15px; }
          #form-gestion .col { flex:1; }
          #form-gestion .lbl { display:block; margin-bottom:6px; color:#555; font-size:14px; font-weight:500; }
        </style>
        <div id="form-gestion" style="text-align:left;padding:15px;">
          <div style="background:#f8f9fa;padding:15px;border-radius:8px;border-left:4px solid #3498db;margin-bottom:20px;">
            <strong style="display:block;margin-bottom:8px;color:#2c3e50;">${visitante.nombreDocumentoVisitante || ""}</strong>
            <p style="color:#7f8c8d;margin:0;font-size:14px;">${visitante.casa || ""}</p>
            <p style="color:#7f8c8d;margin:5px 0 0;font-size:14px;">Doc: ${visitante.numeroDocumentoVisitante || ""}</p>
            <p style="color:#7f8c8d;margin:5px 0 0;font-size:14px;">Estado actual: <strong>${visitante.estado || ""}</strong></p>
          </div>

          <div style="margin-bottom:15px;">
            <label class="lbl">Cambiar estado</label>
            <select id="gEstado" class="swal2-select">
              ${ESTADOS.map(e => opcion(e, estadoSugerido)).join("")}
              ${opcion("Anulado", estadoSugerido)}
            </select>
          </div>

          <div class="fila">
            <div class="col">
              <label class="lbl">Eps</label>
              <input id="gEps" class="swal2-input" value="${visitante.eps || ""}">
            </div>
            <div class="col">
              <label class="lbl">Arl</label>
              <input id="gArl" class="swal2-input" value="${visitante.arl || ""}">
            </div>
          </div>

          <div style="margin-bottom:15px;">
            <label class="lbl">Dispositivo</label>
            <input id="gDispositivo" class="swal2-input" value="${visitante.dispositivo || ""}">
          </div>

          <div class="fila">
            <div class="col">
              <label class="lbl">Marca</label>
              <input id="gMarca" class="swal2-input" value="${visitante.marca || ""}">
            </div>
            <div class="col">
              <label class="lbl">Serial</label>
              <input id="gSerial" class="swal2-input" value="${visitante.serial || ""}">
            </div>
          </div>

          <div style="margin-bottom:15px;">
            <label class="lbl">Tipo de ingreso</label>
            <select id="gTipoIngreso" class="swal2-select">
              ${TIPOS_INGRESO.map(t => opcion(t, tipoIngresoActual)).join("")}
            </select>
          </div>

          <div id="gBloqueVehiculo" class="fila">
            <div class="col">
              <label class="lbl">Tipo de vehículo</label>
              <select id="gTipoVehiculo" class="swal2-select">
                ${TIPOS_VEHICULO.map(t => opcion(t, tipoVehiculoActual)).join("")}
              </select>
            </div>
            <div class="col">
              <label class="lbl">Placa</label>
              <input id="gPlaca" class="swal2-input" value="${visitante.placasVehiculo || ""}" style="text-transform:uppercase;">
            </div>
          </div>
        </div>
      `,
      didOpen: () => {
        const selIngreso = document.getElementById("gTipoIngreso");
        const bloque = document.getElementById("gBloqueVehiculo");
        const alternar = () => {
          const sinVehiculo = ["Peatonal", "Bicicleta"].includes(selIngreso.value);
          bloque.style.display = sinVehiculo ? "none" : "flex";
        };
        selIngreso.addEventListener("change", alternar);
        alternar();
      },
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const estado = document.getElementById("gEstado").value;
        const estadoActual = visitante.estado;

        if (["Pendiente", "Notificado"].includes(estadoActual) && estado === "Salida") {
          Swal.showValidationMessage(
            "Primero, es necesario registrar la entrada del visitante antes de autorizar su salida."
          );
          return false;
        }
        if (estado === "Anulado" && !["Pendiente", "Notificado", "Salida"].includes(estadoActual)) {
          Swal.showValidationMessage("No se puede anular un pase con el visitante adentro. Registre primero la salida.");
          return false;
        }
        if (estadoActual === "Ingreso" && ["Pendiente", "Notificado"].includes(estado)) {
          Swal.showValidationMessage(
            `No es posible cambiar el estado a '${estado}' de un pase que ya ha sido registrado como '${estadoActual}'.`
          );
          return false;
        }

        return {
          estado,
          eps: document.getElementById("gEps").value.trim(),
          arl: document.getElementById("gArl").value.trim(),
          dispositivo: document.getElementById("gDispositivo").value.trim(),
          marca: document.getElementById("gMarca").value.trim(),
          serial: document.getElementById("gSerial").value.trim(),
          tipoIngreso: document.getElementById("gTipoIngreso").value,
          tipoVehiculo: document.getElementById("gTipoVehiculo").value,
          placasVehiculo: document.getElementById("gPlaca").value.trim().toUpperCase(),
        };
      }
    });

    if (!datos) return;

    try {
      const nit = localStorage.getItem("CodigoCliente") || "900362633";
      const [año, mes] = obtenerYearMes(visitante);
      const ruta = rutaPase(nit, visitante.tipoPermiso, año, mes, visitante.id);

      const autorizo = localStorage.getItem("NombreCompleto") || "";
      const fechaHora = fechaHoraDart();

      const cambios = {
        eps: datos.eps,
        arl: datos.arl,
        dispositivo: datos.dispositivo,
        marca: datos.marca,
        serial: datos.serial,
        placasVehiculo: datos.placasVehiculo,
        tipoIngreso: datos.tipoIngreso,
        tipoVehiculo: datos.tipoVehiculo,
        estado: datos.estado,
      };

      if (datos.estado === "Ingreso") {
        cambios.autorizaIngreso = autorizo;
        cambios.fechaHoraIngreso = fechaHora;
      } else if (datos.estado === "Salida") {
        cambios.autorizaSalida = autorizo;
        cambios.fechaHoraSalida = fechaHora;
      } else if (datos.estado === "Anulado") {
        cambios.anulaIngreso = autorizo;
        cambios.fechaHoraAnulacion = fechaHora;
      }

      await actualizarMultiple(
        Object.fromEntries(
          Object.entries(cambios).map(([clave, valor]) => [`${ruta}/${clave}`, valor])
        )
      );

      // La app no avisa ni guarda historial al anular (detalle_pase.dart → _mostrarDialogoEliminar).
      const anulado = datos.estado === "Anulado";

      if (!anulado) {
        await actualizarRegistro(
          `coordi_ph_clientes/${nit}/fcm_visitante/${datos.estado.toLowerCase()}/${limpiarDireccion(visitante.casa)}`,
          { id: visitante.id, fecha: fechaHora }
        );
      }

      const aviso = ["Ingreso", "Salida"].includes(datos.estado)
        ? await notificarResidente(nit, visitante, datos.estado)
        : "";

      if (!anulado && ["PERMANENTE", "LIMITADO"].includes(visitante.tipoPermiso)) {
        const historico = {
          estado: datos.estado,
          autorizo,
          fechaHora,
        };
        if (datos.estado === "Ingreso") {
          historico.autorizaIngreso = autorizo;
          historico.fechaHoraIngreso = fechaHora;
        } else if (datos.estado === "Salida") {
          historico.autorizaSalida = autorizo;
          historico.fechaHoraSalida = fechaHora;
        }
        await crearRegistro(
          rutaHistoricoPase(nit, visitante.tipoPermiso, visitante.id),
          historico
        );
      }

      Swal.fire({
        title: "Actualizado",
        html: `Estado cambiado a ${datos.estado}${aviso}`,
        icon: "success",
        // Con aviso de correo la ventana espera a que la cierren, para que alcance a leerse.
        ...(aviso ? { confirmButtonText: "Aceptar" } : { timer: 2000, showConfirmButton: false }),
      });

      capturarDatos();
    } catch (error) {
      console.error("Error al actualizar estado:", error);
      Swal.fire("Error", "No se pudo actualizar el estado", "error");
    }
  }, [capturarDatos]);

  const facturarVisitante = useCallback(async (visitante) => {
    const seEnvio = await generarFactura(visitante);
    if (seEnvio) capturarDatos();
  }, [capturarDatos]);

  const abrirArchivos = useCallback(async (filasVisibles) => {
    const guardo = await abrirMenuArchivos({
      menu: menuArchivosVisitantes,
      columnas: COLUMNAS,
      filas: filasVisibles,
      archivoCsv: "listado_visitantes.csv",
      descargarPlantilla: descargarPlantillaVisitantes,
      cargar: asistenteCargarVisitantes,
    });
    if (guardo) await capturarDatos();
  }, [capturarDatos]);

  // Un solo botón por fila; las acciones se eligen en una ventana como la de archivos.
  const abrirAcciones = async (fila) => {
    const opcion = await menuAccionesVisitante({
      visitante: fila,
      puedeGestionar: esPorteria(),
      puedeFacturar: puedeFacturar(),
      puedeEnviarCorreo: ROLES_ENVIO_PASE.includes(localStorage.getItem("Permisos")),
    });
    if (opcion === "gestionar") await editarVisitante(fila);
    if (opcion === "factura") await facturarVisitante(fila);
    if (opcion === "correo") await enviarPasePorCorreo(fila);
    if (opcion === "historial") {
      const historial = await consultarSimple(rutaHistoricoPase(nitDePase(fila), fila.tipoPermiso, fila.id));
      await historialPase(fila, movimientosHistorial(historial));
    }
    // El historial de ingresos y salidas del pase se borra con él.
    if (opcion === "eliminar" && await eliminarConConfirmacion({
      fila, singular: "este pase", descripcion: `${fila.nombreDocumentoVisitante || "Visitante"} · ${fila.casa || ""}`,
      rutasExtra: [rutaHistoricoPase(nitDePase(fila), fila.tipoPermiso, fila.id)],
    })) await capturarDatos();
  };

  useEffect(() => {
    capturarDatos();
  }, [capturarDatos]);

  if (cargando) return <AnimacionCarga />;

  const datosFiltradosPorEstado = filtrarPorEstado(datos, filtroEstado);

  return (
    <>
      <TablaGenerica
        datos={datosFiltradosPorEstado}
        titulos={COLUMNAS.map(c => (c.corto || c.label).toUpperCase())}
        campos={COLUMNAS.map(c => c.key)}
        elementosPorPagina={6000}
        tamanoLetra="0.78rem"
        renderizadores={RENDERIZADORES}
        estilosColumnas={COLUMNAS.map(c => c.estilo)}
        columnaOrdenInicial={COLUMNAS.findIndex(c => c.key === "fechaCreacionRegistro")}
        direccionOrdenInicial="desc"
        contenidoExtra={
          <BarraEstados datos={datos} filtro={filtroEstado} onFiltrar={setFiltroEstado}>
            <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
            <BotonArchivos onClick={() => abrirArchivos(datosFiltradosPorEstado)} />
            <button onClick={crearVisitante} title="Nuevo Pase"><i className="bi bi-plus-lg" /></button>
          </BarraEstados>
        }
        botones={botonOpciones(abrirAcciones)}
        tituloBotones="ACCIONES"
      />

      <BotonSOS />
    </>
  );
});

export default ListadoVisitantes;